import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Printer } from 'lucide-react';
import { formatDate, formatTime } from '../utils/dateTime';

type ShareStep = {
  id: string;
  name: string;
  setup: string;
  execution: string;
  equipment: string;
  video_url: string;
};

type SharePositionGroup = {
  group_name: string;
  coaches?: string[];
  steps: ShareStep[];
};

type SharePracticeResponse = {
  practice: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    location: string;
    theme: string;
  };
  drills: Array<{
    id: string;
    name: string;
    duration: number;
    description: string;
    focus_area: string;
    intensity_level: string | null;
    position_groups: SharePositionGroup[];
  }>;
};

const TIME_FMT = '12h' as const;

function clockToMinutes(clock: string): number {
  const p = clock.trim().split(':').map((x) => parseInt(x, 10));
  return (p[0] || 0) * 60 + (p[1] || 0);
}

function minutesToClock(total: number): string {
  const t = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function buildOutlineItems(data: SharePracticeResponse) {
  let cursor = clockToMinutes(data.practice.start_time);
  const items: Array<{
    key: string;
    slot: string;
    drillName: string;
    duration: number;
    focusArea: string;
    drillDescription: string;
    positionGroups: SharePositionGroup[];
  }> = [];

  for (const drill of data.drills) {
    const slotStart = cursor;
    const slotEnd = cursor + drill.duration;
    cursor = slotEnd;
    const slot = `${formatTime(minutesToClock(slotStart), TIME_FMT)}–${formatTime(minutesToClock(slotEnd), TIME_FMT)}`;

    items.push({
      key: `${drill.id}-${items.length}`,
      slot,
      drillName: drill.name,
      duration: drill.duration,
      focusArea: drill.focus_area || '',
      drillDescription: drill.description || '',
      positionGroups: drill.position_groups
    });
  }

  return items;
}

export default function SharePractice() {
  const { token } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<SharePracticeResponse | null>(null);
  const [showFullDetails, setShowFullDetails] = useState(false);

  const shareUrl = useMemo(() => {
    if (!token) return null;
    return `${window.location.origin}/share/practice/${token}`;
  }, [token]);

  const outlineItems = useMemo(() => (data ? buildOutlineItems(data) : []), [data]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
        const url = `${supabaseUrl}/functions/v1/share-practice?token=${encodeURIComponent(token)}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: anon ? { apikey: anon } : undefined
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.error || 'Unable to load shared practice');
        }
        setData(json as SharePracticeResponse);
      } catch (e: unknown) {
        console.error('Error loading shared practice:', e);
        const msg = e instanceof Error ? e.message : 'Unable to load shared practice';
        toast.error(msg);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        <p className="text-stone mt-4">Loading shared practice…</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-lg w-full">
          <h1 className="text-xl font-semibold mb-2">Invalid link</h1>
          <p className="text-gray-600">This share link is missing a token.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-lg w-full">
          <h1 className="text-xl font-semibold mb-2">Link unavailable</h1>
          <p className="text-gray-600">
            This shared practice link is invalid, expired, or has been revoked.
          </p>
          {shareUrl && <p className="text-xs text-gray-400 mt-3 break-all">{shareUrl}</p>}
        </div>
      </div>
    );
  }

  const practiceWindow = `${formatTime(data.practice.start_time, TIME_FMT)} – ${formatTime(data.practice.end_time, TIME_FMT)}`;

  return (
    <div className="share-practice-print-root min-h-screen bg-cream px-3 py-4 sm:px-4 sm:py-6 print:bg-white print:py-2">
      <div className="max-w-6xl mx-auto">
        <header className="share-practice-meta bg-white rounded-lg shadow-md p-4 mb-4 print:shadow-none flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{data.practice.theme}</h1>
            <div className="share-practice-meta-grid mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600">
              <div>
                <span className="font-medium text-gray-700">Date: </span>
                {formatDate(data.practice.date)}
              </div>
              <div>
                <span className="font-medium text-gray-700">Practice window: </span>
                {practiceWindow}
              </div>
              <div className="sm:col-span-2">
                <span className="font-medium text-gray-700">Location: </span>
                {data.practice.location}
              </div>
              <div className="text-xs text-gray-500 sm:col-span-2">Times below are sequential blocks from practice start.</div>
            </div>
          </div>
          <div className="share-practice-no-print flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-200"
              onClick={() => setShowFullDetails((v) => !v)}
              aria-label={showFullDetails ? 'Hide full drill details' : 'Show full drill details'}
            >
              {showFullDetails ? 'Hide details' : 'Show details'}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-200"
              onClick={() => window.print()}
              aria-label="Print practice plan"
            >
              <Printer className="w-4 h-4" aria-hidden />
              Print
            </button>
          </div>
        </header>

        <div className="share-practice-outline rounded-lg border border-gray-200 bg-white shadow-sm print:shadow-none print:border-gray-800">
          {outlineItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-500">No drills on this practice.</div>
          ) : (
            <ol className="m-0 p-3 sm:p-4 space-y-2 print:space-y-1 list-none">
              {outlineItems.map((item, idx) => (
                <li
                  key={item.key}
                  className={`share-practice-card border rounded-lg p-3 print:p-1.5 print:border-gray-700 ${
                    item.drillName.toLowerCase().includes('water break')
                      ? 'bg-blue-50 border-blue-200'
                      : (item.drillName.toLowerCase().includes('warm up') || item.drillName.toLowerCase().includes('warmup'))
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <div className="share-practice-time font-semibold text-gray-900 whitespace-nowrap">{item.slot}</div>
                        <div className="share-practice-title font-semibold text-gray-900 truncate">
                          {idx + 1}. {item.drillName}
                        </div>
                        {!item.drillName.toLowerCase().includes('water break') ? (
                          <>
                            <div className="text-gray-600 text-xs whitespace-nowrap">{item.duration} min</div>
                            {item.focusArea ? <div className="text-gray-500 text-xs truncate">{item.focusArea}</div> : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Water breaks: one-line only */}
                  {item.drillName.toLowerCase().includes('water break') ? null : (
                    <>
                      {/* Cliff notes: always show step names (compact). */}
                      <div className="mt-2 space-y-2">
                        {item.positionGroups.length === 0 ? (
                          <div className="text-xs text-gray-500">No position group details.</div>
                        ) : (
                          item.positionGroups.map((pg) => (
                            <div key={pg.group_name} className="text-xs text-gray-800">
                              <div className="font-medium">
                                {pg.group_name}{' '}
                                {pg.coaches?.length ? (
                                  <span className="text-gray-500">({pg.coaches.join(', ')})</span>
                                ) : (
                                  <span className="text-gray-500">(All coaches)</span>
                                )}
                              </div>
                              {pg.steps.length ? (
                                <ul className="mt-1 list-disc pl-5 space-y-0.5 text-gray-700">
                                  {pg.steps.map((s) => (
                                    <li key={s.id} className="truncate" title={s.name}>
                                      {s.name}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-gray-500 mt-0.5">No steps.</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      {showFullDetails ? (
                        <div className="share-practice-details mt-2 border-t border-gray-100 pt-2 space-y-2">
                          {item.drillDescription ? (
                            <p className="share-practice-desc text-xs text-gray-800 leading-snug whitespace-pre-wrap">
                              {item.drillDescription}
                            </p>
                          ) : null}
                          {item.positionGroups.length === 0 ? (
                            <div className="text-xs text-gray-500">No position group details.</div>
                          ) : (
                            item.positionGroups.map((pg) => (
                              <div key={pg.group_name} className="text-xs text-gray-800">
                                <div className="font-medium">
                                  {pg.group_name}{' '}
                                  {pg.coaches?.length ? (
                                    <span className="text-gray-500">({pg.coaches.join(', ')})</span>
                                  ) : (
                                    <span className="text-gray-500">(All coaches)</span>
                                  )}
                                </div>
                                {pg.steps.length ? (
                                  <ul className="mt-1 list-disc pl-5 space-y-0.5 text-gray-700">
                                    {pg.steps.map((s) => (
                                      <li key={s.id}>
                                        <span className="font-medium text-gray-800">{s.name}</span>
                                        {s.equipment ? <span className="text-gray-600"> — {s.equipment}</span> : null}
                                        {s.video_url ? (
                                          <>
                                            {' '}
                                            <a
                                              href={s.video_url}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-primary hover:underline"
                                            >
                                              Video
                                            </a>
                                          </>
                                        ) : null}
                                        {(s.setup || s.execution) ? (
                                          <div className="mt-0.5 text-gray-700">
                                            {s.setup ? (
                                              <div>
                                                <span className="font-medium text-gray-800">Setup:</span> {s.setup}
                                              </div>
                                            ) : null}
                                            {s.execution ? (
                                              <div>
                                                <span className="font-medium text-gray-800">Execution:</span> {s.execution}
                                              </div>
                                            ) : null}
                                          </div>
                                        ) : null}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-gray-500 mt-0.5">No steps.</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        <p className="share-practice-footer text-center text-xs text-gray-500 mt-4 print:mt-1">
          Shared from Hustli · Read-only
        </p>
      </div>
    </div>
  );
}
